import { FC, useState } from 'react';
import { ReactComponent as BurgerIcon } from '@/assets/icons/burgerMenu.svg';
import { useNavigate } from 'react-router';
import { ROUTES } from '@/types/routes';
import { Logo } from '@/components/logo';
import { MobileHeader } from '@/components/layout/MobileHeader';
import { Button, Drawer, Flex, Grid } from 'antd';
import { CustomButton } from '@/components/ui';
import { ReactComponent as XClose } from '@/assets/icons/x-close.svg';
import { UserRole } from '@/types';
import { jwtDecode } from 'jwt-decode';

import './Header.scss';

interface MenuItem {
  label: string;
  path: string;
}

const Header: FC = () => {
  const { lg } = Grid.useBreakpoint();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const isAuthenticated = !!localStorage.getItem('accessToken');
  let decodedToken: { role: UserRole } | undefined;
  if (isAuthenticated) {
    try {
      decodedToken = jwtDecode(localStorage.getItem('accessToken') || '') as {
        role: UserRole;
      };
    } catch (error) {
      console.log(error);
    }
  }

  const menu: MenuItem[] = [
    { label: 'How It Works', path: '#how-it-works' },
    { label: 'Find Your Role', path: '#find-your-role' },
    { label: 'Features', path: '#features' },
    { label: 'FAQs', path: '#faq' },
  ];

  const handleToggleMenu = () => setMenuOpen((prev) => !prev);

  const handleBackToPlatform = () =>
    navigate(
      decodedToken?.role === UserRole.CONSULTANT
        ? ROUTES.FIND_FRANCHISORS
        : ROUTES.FIND_CONSULTANT,
    );

  const handleScroll = (
    event: React.MouseEvent<HTMLAnchorElement>,
    path: string,
  ) => {
    event.preventDefault();
    const targetElement = document.querySelector(path);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className={'landing-header'}>
      {lg ? (
        <Flex
          justify={'space-between'}
          align={'center'}
          className={'container'}
        >
          <Flex align={'center'}>
            <Logo />
          </Flex>

          <nav className={'navigation'}>
            {menu.map((item) => (
              <a
                key={item.path}
                href={item.path}
                className={'navigation-item'}
                onClick={(e) => handleScroll(e, item.path)}
              >
                {item.label}
              </a>
            ))}
          </nav>

          {!isAuthenticated ? (
            <Flex gap={8}>
              <CustomButton
                text={'SIGN UP'}
                type={'default'}
                onClick={() => navigate(ROUTES.SIGN_UP)}
              />

              <CustomButton
                text={'LOG IN'}
                type={'primary'}
                onClick={() => navigate(ROUTES.SIGN_IN)}
              />
            </Flex>
          ) : (
            <Flex gap={8}>
              <CustomButton
                text={'BACK TO PLATFORM'}
                type={'primary'}
                onClick={handleBackToPlatform}
              />
            </Flex>
          )}
        </Flex>
      ) : (
        <MobileHeader
          actions={
            <Button onClick={handleToggleMenu} className={'burger-menu-button'}>
              <BurgerIcon />
            </Button>
          }
        />
      )}

      <Drawer
        open={menuOpen}
        onClose={handleToggleMenu}
        className={'landing-menu-drawer'}
        closable={false}
        title={
          <Flex justify={'space-between'} align={'center'}>
            <Logo />

            <XClose onClick={handleToggleMenu} className={'close-button'} />
          </Flex>
        }
      >
        <nav className={'mobile-navigation'}>
          {menu.map((item) => (
            <a
              key={item.path}
              className={'navigation-item'}
              onClick={(e) => {
                handleScroll(e, item.path);
                handleToggleMenu();
              }}
            >
              {item.label}
            </a>
          ))}
        </nav>

        <Flex vertical gap={16} className={'auth-buttons'}>
          {isAuthenticated ? (
            <CustomButton
              text={'BACK TO PLATFORM'}
              type={'primary'}
              onClick={handleBackToPlatform}
            />
          ) : (
            <>
              <CustomButton
                text={'SIGN UP'}
                type={'default'}
                onClick={() => navigate(ROUTES.SIGN_UP)}
              />

              <CustomButton
                text={'LOG IN'}
                type={'primary'}
                onClick={() => navigate(ROUTES.SIGN_IN)}
              />
            </>
          )}
        </Flex>
      </Drawer>
    </div>
  );
};

export { Header };
